import { TableProperties } from 'lucide-react';
import type { KpiCatalogueEntry } from '../../types/strategicKpis';
import { BADGE_CLASS, DOT_CLASS, STATUS_LABEL, formatAchievement, formatKpiValue, judgedOnYtdShare, noDataText, priorDirection, priorWord, type Light, type PriorDirection } from '../../utils/strategicKpiFormat';
import type { JudgedValue } from '../../utils/strategicKpiSelectors';

/**
 * Small pieces the dashboard repeats (K5, design update after the audit of 2026-10-07): one status, one
 * prior-year cue, one "rows" button and one pill, so the steered rows, the ranking and the context cards cannot
 * drift apart again. Since the redesign (masterplan skpi-redesign B1) they are drawn with the scoped kit
 * (`styles/skpi/kit.css`): colour, type and borders from kit classes and tokens, Tailwind only for layout.
 *
 * The text helpers (`actualText`, `targetText`, `statusText`, `priorText`) are what a cell shows AND what the
 * `data-skpi-*` attributes carry (scripts/skpi-compare.mjs reads them): one function, so the attribute can
 * never differ from the figure on screen.
 */

/** A toggle in a row of toggles: the kit's `.pill` (36 px, ultramarine when `aria-pressed`). The caller sets `aria-pressed`. */
export function chipClass(_active: boolean): string {
    return 'pill';
}

/** The actual as the cell shows it — «No data» for a null (the reason sits in the tooltip), else `formatKpiValue`. */
export function actualText(kpi: Pick<KpiCatalogueEntry, 'unit'>, j: Pick<JudgedValue, 'actual'>): string {
    return j.actual === null ? 'No data' : formatKpiValue(kpi.unit, j.actual);
}

/** What the achievement is judged against: visits and GP amounts «YTD x · year y» (the YTD share and the year target), every other KPI the target. */
export function targetText(kpi: Pick<KpiCatalogueEntry, 'unit' | 'category' | 'targetProrated'>, j: Pick<JudgedValue, 'target' | 'targetYtd'>): string {
    return judgedOnYtdShare(kpi)
        ? `YTD ${formatKpiValue(kpi.unit, j.targetYtd)} · year ${formatKpiValue(kpi.unit, j.target)}`
        : formatKpiValue(kpi.unit, j.target);
}

/**
 * The status as words: «92 % · behind», «no pace», «no target», or — ND-03 — «52 % of the year target» for a KPI
 * judged at the year end.
 */
export function statusText({ light, achievement, hasTarget = true, atYearEnd = false }: { light: Light; achievement: number | null; hasTarget?: boolean; atYearEnd?: boolean }): string {
    if (atYearEnd && hasTarget && achievement !== null) return `${formatAchievement(achievement, true)} of the year target`;
    if (!hasTarget) return 'no target';
    return achievement === null ? STATUS_LABEL.none : `${formatAchievement(achievement, true)} · ${STATUS_LABEL[light]}`;
}

/** The cue as words — also the screen-reader text of `PriorCue`. */
export function priorCueText(dir: PriorDirection): string {
    const word = priorWord(dir);
    return `${dir.arrow} ${word}${word === 'same' ? ' as the prior year' : ' than the prior year'}`;
}

/**
 * The prior-year cell as one string: «PY 1'234» followed, without a space, by the cue «▲ better than the prior year» —
 * exactly the text content of the cell (the cue sits in a block under the value), which the comparison reads. Never
 * a difference (K4/K5, masterplan §2 rule 8).
 */
export function priorText(kpi: Pick<KpiCatalogueEntry, 'unit' | 'direction' | 'windowType'>, j: Pick<JudgedValue, 'actual' | 'prior'>): string {
    const dir = priorDirection(kpi, j.actual, j.prior);
    return `PY ${priorValueText(kpi, j)}${dir ? priorCueText(dir) : ''}`;
}

/** The prior-year figure alone: the value, «—» for none, «no status» for a status KPI without a prior-year reading. */
export function priorValueText(kpi: Pick<KpiCatalogueEntry, 'unit' | 'windowType'>, j: Pick<JudgedValue, 'prior'>): string {
    return j.prior === null ? (kpi.windowType === 'POINT' ? 'no status' : '—') : formatKpiValue(kpi.unit, j.prior);
}

/**
 * The status of one pace: neutral text whose dot alone carries the light (ND-01), the words next to it (never
 * colour alone). `atYearEnd` (ND-03): no light (hollow dot), «n % of the year target».
 */
export function StatusBadge({ light, achievement, hasTarget = true, noData, atYearEnd = false, title }: {
    light: Light;
    achievement: number | null;
    hasTarget?: boolean;
    noData?: string;
    atYearEnd?: boolean;
    title?: string;
}) {
    const yearEnd = atYearEnd && hasTarget && achievement !== null;
    const text = statusText({ light, achievement, hasTarget, atYearEnd });
    if (yearEnd) {
        return (
            <span
                className={`inline-flex items-center gap-2 whitespace-nowrap ${BADGE_CLASS.none}`}
                title={title ?? 'A year-to-date count against the full-year target — judged at the year end, so no light until then'}
                data-achievement
                data-year-end
            >
                <span className={DOT_CLASS.none} aria-hidden="true" />
                {text}
            </span>
        );
    }
    const why = !hasTarget ? 'No target set for this year' : achievement === null ? (noData ? noDataText(noData) : 'The target is 0 — no pace can be judged') : undefined;
    return (
        <span className={`inline-flex items-center gap-2 whitespace-nowrap ${BADGE_CLASS[light]}`} title={title ?? why} data-achievement>
            <span className={DOT_CLASS[hasTarget ? light : 'none']} aria-hidden="true" />
            {hasTarget && achievement !== null ? <><span className="strong">{formatAchievement(achievement, true)}</span> · {STATUS_LABEL[light]}</> : text}
        </span>
    );
}

/** ND-01 / S1-06: the arrow is neutral and says what it means — «▲ worse» for Avg Days going up. */
export function PriorCue({ dir }: { dir: PriorDirection | null }) {
    if (!dir) return null;
    const word = priorWord(dir);
    return (
        <span className="muted whitespace-nowrap" title={word === 'same' ? 'Same as the prior year' : `${word === 'better' ? 'Better' : 'Worse'} than the prior year`}>
            {' '}<span aria-hidden="true">{dir.arrow} </span>{word}<span className="sr-only">{word === 'same' ? ' as the prior year' : ' than the prior year'}</span>
        </span>
    );
}

/**
 * The button behind every figure that opens its rows (S2-05 32 px, S2-08 named for what it opens, S3-05 a table
 * icon — `ExternalLink` is kept for the real HubSpot link inside the evidence view).
 */
export function RowsButton({ onClick, name }: { onClick: () => void; name: string }) {
    return (
        <button type="button" onClick={onClick} aria-label={name} title={name} className="btn quiet sm">
            <TableProperties className="i sm" aria-hidden="true" /> rows
        </button>
    );
}
